/**
 * printDocumentTitle.ts — the printed page is named after the RECORD, not "React App".
 *
 * WHY. Chrome puts `document.title` in the printed page header AND uses it as the default
 * filename when the user saves as PDF. The app's tab title is the CRA default, so a printed
 * deal came out headed "React App" and saved as `React App.pdf` — measured on the Amazon
 * deal, 2026-07-27. The document itself was correct; only its name was wrong.
 *
 * NOT A SECOND PRINT MECHANISM. There is exactly one print lifecycle in this app — the
 * `beforeprint` / `afterprint` listeners `Drawer` registers for `printable` drawers — and
 * these two functions are called from inside it. They add no listeners of their own.
 *
 * MODULE-LEVEL STATE, DELIBERATELY. A drawer stays mounted through its 360ms slide-out, so
 * two printable drawers can briefly overlap and both handlers fire. Per-instance state would
 * let the second one "save" the title the first had already swapped in, and the restore would
 * then put the RECORD'S name back as the app's permanent tab title — the exact failure the
 * spec calls out ("the app's tab must not stay renamed"). One saved value, written once and
 * cleared on restore, makes apply/restore idempotent no matter how many callers there are.
 *
 * IT ONLY RENAMES FOR A RECORD SHEET. `printedRecordTitle` returns null when the document
 * holds no `.p-record-sheet`, and a null means "leave the tab alone". So the three
 * sheet-bearing drawers (deal, lead, contact) rename; the six other printable drawers —
 * task detail, invoice, quote, org-chart profile, KPI comments — are untouched, because
 * naming their printouts is a separate decision nobody has made.
 *
 * ── AND PAGES, ADDED 2026-07-28 ───────────────────────────────────────────────────────
 * Printing a LIST — no drawer, no document portal, just Ctrl+P on a page — went through
 * none of the above, so a printed contacts list came out headed "React App" and saved as
 * `React App.pdf`. `printedPageTitle` names it after the page's own <h1>, and the app
 * shell calls the SAME apply/restore pair below. Still one mechanism and one saved value.
 *
 * THE PAGE NAME IS THE WEAKEST CLAIM ON THE TITLE, and the shell enforces that by not
 * asking when something more specific owns the print: a printable drawer is open, or a
 * document path (invoice / org chart) is running. It cannot be enforced here by "first
 * caller wins", because the shell's listener is registered before any drawer's and would
 * therefore win every race — which would have printed an open deal record titled
 * "Deal Pipeline". See DashboardLayout.
 */

/** What the tab is called while printing a record that has no title of its own. Sensible
 *  rather than clever: it is what the printed thing IS, and it makes a legible filename
 *  ("Record.pdf"). Reached only when the sheet renders no <h1>, which happens when the host
 *  passes an empty title — a contact or lead saved with no name at all. */
export const PRINT_TITLE_FALLBACK = 'Record';

/**
 * What the printed document should be called.
 *
 * TWO SOURCES, IN THIS ORDER, and the order is the design:
 *
 *  1. An EXPLICIT name, passed by the surface being printed. Needed because most printable
 *     surfaces are not record sheets — an invoice wants its NUMBER (that is what a customer
 *     searches for), a quote its number, a task its title. Nothing on those screens is a
 *     `.p-record-sheet__title` to read.
 *  2. Otherwise the record sheet's own `<h1>`. For the four CRM sheets that is strictly
 *     better than an explicit string: the tab name and the heading on the paper are the
 *     same text BY CONSTRUCTION and cannot drift apart.
 *
 * Returns `null` when neither exists — meaning "leave the tab alone", which is what a
 * printable drawer with no sheet and no explicit name gets.
 */
export function printedRecordTitle(doc: Document = document, explicit?: string | null): string | null {
  const named = (explicit ?? '').trim().replace(/\s+/g, ' ');
  if (named) return named;
  const sheet = doc.querySelector('.p-record-sheet');
  if (!sheet) return null;
  const heading = (sheet.querySelector('.p-record-sheet__title')?.textContent ?? '')
    .trim().replace(/\s+/g, ' ');
  return heading || PRINT_TITLE_FALLBACK;
}

/**
 * What a printed PAGE should be called — the page's own <h1>.
 *
 * Read from the DOM rather than kept in a route→name map, for the reason
 * `printedRecordTitle` reads the sheet's heading: the tab name and the heading on the
 * paper are then the same text BY CONSTRUCTION and cannot drift. It also means every
 * route is covered the day it is written, with nothing to wire per page — 91 of the
 * app's 97 pages carry an <h1> today.
 *
 * `.p-page-title` is preferred where a page uses the design-system class, then any <h1>
 * inside <main>. Scoped to `<main>` deliberately: a heading in the sidebar, the topbar or
 * an open drawer is not what this page IS.
 *
 * Returns null when the page has no heading — the same "leave the tab alone" contract the
 * record version uses, rather than inventing a name for a page that never states one.
 */
export function printedPageTitle(doc: Document = document): string | null {
  const main = doc.querySelector('.app-shell > main');
  if (!main) return null;
  const heading = main.querySelector('.p-page-title') ?? main.querySelector('h1');
  const text = (heading?.textContent ?? '').trim().replace(/\s+/g, ' ');
  return text || null;
}

/** The tab's own title, held from `beforeprint` to `afterprint`. Null = nothing swapped. */
let savedTitle: string | null = null;

/**
 * `beforeprint` half: remember the tab's title and rename it to the record's.
 *
 * No-op when the document holds no record sheet, and no-op when a swap is already in
 * flight — never save over the saved value, or the restore puts a record name back.
 */
export function applyPrintTitle(explicit?: string | null, doc: Document = document): void {
  if (savedTitle !== null) return;
  const next = printedRecordTitle(doc, explicit);
  if (next == null) return;
  savedTitle = doc.title;
  doc.title = next;
}

/**
 * `afterprint` half (and the drawer's unmount cleanup): put the previous title back.
 * Safe to call when nothing was swapped, and safe to call twice.
 */
export function restorePrintTitle(doc: Document = document): void {
  if (savedTitle === null) return;
  doc.title = savedTitle;
  savedTitle = null;
}

/**
 * Print the WHOLE PAGE under a given name — for the two surfaces that print by calling
 * `window.print()` straight from a button (custom reports, weekly reports) rather than
 * through a drawer or a document portal.
 *
 * It exists so those two do not each hand-roll a listener: same saved-title state, same
 * restore, one place. `afterprint` fires when the dialog is DISMISSED as well as when it
 * completes, so the tab is put back on cancel too — which a `print(); restore();` pair
 * would get wrong in any browser where `window.print()` does not block.
 */
export function printThisPage(name: string, win: Window = window): void {
  applyPrintTitle(name, win.document);
  const done = () => {
    restorePrintTitle(win.document);
    win.removeEventListener('afterprint', done);
  };
  win.addEventListener('afterprint', done);
  win.print();
}
